/**
 * AppLayout — the authenticated shell (spec U3.1): AppBar + Drawer + Outlet, ported from
 * saas-admin's layout. Mounted inside the AuthGate, so it only ever renders with a live
 * session.
 *
 * The drawer is permanent on md+ and a temporary overlay below that. Nav is exactly the two
 * feature surfaces and nothing else. A principal the brand API would reject still gets the
 * shell — `BrandAccessGate` decides what the content area renders — so an operator or a
 * non-owner can always read the account menu and sign out of the wrong account.
 */
import {
  AppBar,
  Box,
  Chip,
  Container,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ViewQuiltIcon from '@mui/icons-material/ViewQuilt';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import { useState, type ReactNode } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useAuth, usePrincipal } from '@/auth/AuthContext';
import { useCopy } from '@/hooks/useCopy';
import { env } from '@/config/env';
import { AvatarDropdown } from './AvatarDropdown';

const DRAWER_WIDTH = 240;

interface NavItem {
  to: string;
  labelKey: string;
  fallback: string;
  icon: ReactNode;
}

/**
 * The two feature surfaces. Login Branding and Site Builder were one editor all along
 * (SASBND-0010): they now share a single entry, and the surface a tenant is editing is a
 * control inside it rather than a nav choice.
 */
const NAV: readonly NavItem[] = [
  {
    to: '/site-builder',
    labelKey: 'nav.site',
    fallback: 'Site',
    icon: <ViewQuiltIcon />,
  },
  {
    to: '/email-templates',
    labelKey: 'nav.emailTemplates',
    fallback: 'Email templates',
    icon: <EmailOutlinedIcon />,
  },
];

export function AppLayout() {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const { t } = useCopy();
  const principal = usePrincipal();
  const { signOut } = useAuth();
  const location = useLocation();
  // Non-prod builds tint the AppBar and show an env chip so staging/local is unmistakable.
  const isNonProd = env.envLabel !== 'production';

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <AppBar
        position="fixed"
        color="default"
        elevation={0}
        sx={{
          borderBottom: isNonProd ? 3 : 1,
          borderColor: isNonProd ? 'warning.main' : 'divider',
          zIndex: (th) => th.zIndex.drawer + 1,
        }}
      >
        <Toolbar>
          {!isDesktop ? (
            <IconButton
              edge="start"
              onClick={() => setMobileOpen((v) => !v)}
              aria-label={t('common.menu', 'Menu')}
            >
              <MenuIcon />
            </IconButton>
          ) : null}
          <Typography
            variant="h1"
            component={NavLink}
            to="/site-builder"
            sx={{
              fontSize: '1.05rem',
              ml: isDesktop ? 0 : 1,
              textDecoration: 'none',
              color: 'inherit',
              fontWeight: 600,
            }}
          >
            {t('app.title', 'Brand Center')}
          </Typography>
          {isNonProd ? (
            <Chip
              label={env.envLabel.toUpperCase()}
              color="warning"
              size="small"
              sx={{ ml: 1.5, fontWeight: 700, letterSpacing: 0.5 }}
            />
          ) : null}
          <Box sx={{ flex: 1 }} />
          {/* Appearance is no longer a top-bar control: it lives under this menu's Settings
              entry (SASBND-0009 U2). */}
          <AvatarDropdown principal={principal} onSignOut={() => void signOut()} />
        </Toolbar>
      </AppBar>
      <Drawer
        variant={isDesktop ? 'permanent' : 'temporary'}
        open={isDesktop ? true : mobileOpen}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          width: DRAWER_WIDTH,
          [`& .MuiDrawer-paper`]: {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
          },
        }}
      >
        <Toolbar />
        <Box
          component="nav"
          aria-label={t('nav.label', 'Main navigation')}
          // The horizontal inset is what lets the rounded active pill read as a pill
          // rather than a full-bleed band — dink-admin's nav-list padding.
          sx={{ width: DRAWER_WIDTH, px: 1.25, py: 1 }}
        >
          <List disablePadding>
            {NAV.map((item) => (
              // ListItem keeps the <li> wrapper the `list` a11y rule requires — a
              // NavLink-as-ListItemButton would otherwise sit as a bare <a> inside the <ul>.
              <ListItem key={item.to} disablePadding>
                <ListItemButton
                  component={NavLink}
                  to={item.to}
                  onClick={() => setMobileOpen(false)}
                  // dink-admin's nav-item geometry. The selected/hover colors are
                  // mode-dependent and live on the theme's `MuiListItemButton`.
                  sx={{ borderRadius: 2, minHeight: 42, mb: '2px' }}
                >
                  <ListItemIcon sx={{ minWidth: 36, '& .MuiSvgIcon-root': { fontSize: 21 } }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={t(item.labelKey, item.fallback)}
                    slotProps={{ primary: { sx: { fontSize: 14, fontWeight: 500 } } }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>
      </Drawer>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
          minHeight: '100vh',
          bgcolor: 'background.default',
        }}
      >
        <Toolbar />
        <Container maxWidth="lg" sx={{ py: 4 }} key={location.pathname}>
          <Outlet />
        </Container>
      </Box>
    </Box>
  );
}
